export const M9_UNIT_7 = {
  id: "m9-u7-zero conditional",
  title: "Unidad 7: Zero conditional",
  topic: "Master question tags to check information, seek agreement, and sound more natural in English conversations about travel, leisure activities, and everyday plans.",
  iconType: "message-circle",
"sets": [
    {
      "set": 1,
      "title": "Laboratory Equipment & Basic Science",
      "context": "Laboratory tools, safety equipment, and fundamental scientific terminology in experimental contexts",
      "questions": [
        {
          "type": "match",
          "q": "Match the laboratory equipment with its function.",
          "matches": [
            {"left": "Thermometer", "right": "Measures temperature"},
            {"left": "Beaker", "right": "Container for liquids in experiments"},
            {"left": "Microscope", "right": "Magnifies small objects"},
            {"left": "Safety goggles", "right": "Protects eyes during experiments"}
          ],
          "tip": "Think about what each tool does in a science lab.",
          "successTip": "We say 'wear goggles' (NOT 'use goggles'). Common collocations: 'safety equipment', 'lab equipment', 'protective gear'. In British English, also called 'safety spectacles'."
        },
        {
          "type": "match",
          "q": "Match the scientific term with its definition.",
          "matches": [
            {"left": "Hypothesis", "right": "A prediction to be tested in an experiment"},
            {"left": "Reaction", "right": "A process where substances change"},
            {"left": "Crystals", "right": "Solid structures with regular patterns"},
            {"left": "Results", "right": "Data collected from an experiment"}
          ],
          "tip": "These are fundamental concepts in scientific method.",
          "successTip": "The verb is 'hypothesize' (to hypothesize that...). We say 'test a hypothesis' (NOT 'prove'), 'chemical reaction' (fixed collocation), and 'analyze results' (NOT 'analyze the data points')."
        },
        {
          "type": "mc",
          "q": "A chemistry teacher says: 'When you heat ice, something happens.' What happens to ice when you heat it?",
          "options": [
            "A) It boils",
            "B) It melts",
            "C) It freezes",
            "D) It evaporates"
          ],
          "a": "B",
          "tip": "Think about what happens to solid water when temperature increases.",
          "successTip": "We say 'ice melts' (NOT 'ice is melting down'). Common expressions: 'melt away' (desaparecer gradualmente), 'melting point' (punto de fusión). The adjective is 'molten' (derretido) for metals."
        },
        {
          "type": "mc",
          "q": "In a science class, a student asks: 'What do we use to measure temperature in experiments?' What is the correct answer?",
          "options": [
            "A) A microscope",
            "B) A beaker",
            "C) A thermometer",
            "D) A magnet"
          ],
          "a": "C",
          "tip": "This instrument has 'thermo' in its name, meaning heat.",
          "successTip": "We say 'take the temperature' or 'measure the temperature' (NOT 'check'). Common phrases: 'room temperature', 'body temperature', 'at a temperature of 50°C'."
        },
        {
          "type": "input",
          "q": "Lab instructor: 'Please put on your safety _____ before we start the experiment.' (eye protection)",
          "a": "goggles",
          "alts": ["glasses"],
          "tip": "These protect your eyes in the laboratory.",
          "successTip": "Always plural: 'goggles' (NOT 'goggle'). Similar pattern: 'glasses', 'scissors', 'pants'. We say 'put on goggles' (wear) or 'take off goggles' (remove)."
        },
        {
          "type": "order",
          "q": "Science teacher explains: 'Cuando calientas agua, hierve.'",
          "words": ["When", "you", "heat", "water,", "it", "boils."],
          "a": "When you heat water, it boils.",
          "extra": ["freezes", "melting"],
          "tip": "This is a zero conditional sentence about a scientific fact.",
          "successTip": "Zero conditional uses present simple in BOTH clauses. 'When' and 'if' are interchangeable here. Common mistake: 'When you will heat' (WRONG - no future in zero conditional)."
        },
        {
          "type": "order",
          "q": "Chemist states: 'El hielo flota en el agua.'",
          "words": ["Ice", "floats", "in", "water."],
          "a": "Ice floats in water.",
          "extra": ["sinks", "melts"],
          "tip": "This describes what happens to ice in liquid water.",
          "successTip": "Verb 'float' is intransitive (no object needed). Opposite is 'sink'. Phrasal verb: 'float around' (dar vueltas sin propósito). We say 'float ON water' (superficie) or 'float IN water' (dentro del líquido)."
        },
        {
          "type": "order",
          "q": "Lab assistant explains: 'Si mezclas azul y amarillo, obtienes verde.'",
          "words": ["If", "you", "mix", "blue", "and", "yellow,", "you", "get", "green."],
          "a": "If you mix blue and yellow, you get green.",
          "extra": ["red", "orange"],
          "tip": "This is about color mixing using zero conditional.",
          "successTip": "Verb 'mix' can be transitive (mix A and B) or intransitive (they don't mix). Phrasal verbs: 'mix up' (confundir), 'mix in' (incorporar). Collocation: 'mix colors/ingredients/sounds'."
        },
        {
          "type": "order",
          "q": "Teacher demonstrates: 'Cuando dejas caer una piedra, cae.'",
          "words": ["When", "you", "drop", "a", "rock,", "it", "falls."],
          "a": "When you drop a rock, it falls.",
          "extra": ["floats", "rises"],
          "tip": "This describes the effect of gravity.",
          "successTip": "Verb 'drop' = let something fall (transitive). 'Fall' = move downward (intransitive). Phrasal verbs: 'drop by' (visitar informalmente), 'drop off' (dejar algo/alguien), 'fall over' (caerse completamente)."
        },
        {
          "type": "mc",
          "q": "A physics professor states: 'If you put oil in water, it doesn't mix. What does the oil do?'",
          "options": [
            "A) It sinks to the bottom",
            "B) It dissolves completely",
            "C) It floats on top",
            "D) It evaporates"
          ],
          "a": "C",
          "tip": "Think about oil's density compared to water.",
          "successTip": "We say 'float ON top' or 'float ON the surface' (NOT 'float at top'). Expression: 'like oil and water' (things that don't mix well). Note: 'on top of' requires 'of' when followed by noun."
        }
      ]
    },
    {
      "set": 2,
      "title": "States of Matter & Physical Changes",
      "context": "Phase transitions, states of matter (solid, liquid, gas), and thermal processes in scientific contexts",
      "questions": [
        {
          "type": "match",
          "q": "Match the state of matter with its characteristics.",
          "matches": [
            {"left": "Solid", "right": "Has fixed shape and volume"},
            {"left": "Liquid", "right": "Takes shape of container but has fixed volume"},
            {"left": "Gas", "right": "No fixed shape or volume"},
            {"left": "Steam", "right": "Water in gas form"}
          ],
          "tip": "Think about how molecules are arranged in each state.",
          "successTip": "Uncountable nouns in science: 'water' (NOT 'waters'), 'ice' (NOT 'ices'), but 'a gas' (countable when referring to types). We say 'in liquid form' or 'in its liquid state'."
        },
        {
          "type": "match",
          "q": "Match the process with its definition.",
          "matches": [
            {"left": "To freeze", "right": "Change from liquid to solid"},
            {"left": "To melt", "right": "Change from solid to liquid"},
            {"left": "To boil", "right": "Change from liquid to gas rapidly"},
            {"left": "To evaporate", "right": "Change from liquid to gas slowly"}
          ],
          "tip": "These are all phase transitions between states of matter.",
          "successTip": "Verb patterns: 'freeze' (can be transitive or intransitive), 'boil' (same), but 'evaporate' (usually intransitive). We say 'the water is boiling' (continuous for action in progress) or 'water boils at 100°C' (simple for general facts)."
        },
        {
          "type": "mc",
          "q": "A science teacher explains: 'When water freezes, it becomes solid.' At what temperature does water freeze?",
          "options": [
            "A) 100°C",
            "B) 50°C",
            "C) 0°C",
            "D) -10°C"
          ],
          "a": "C",
          "tip": "This is the freezing point of pure water.",
          "successTip": "Temperature expressions: 'at zero degrees' (NOT 'in'), 'below freezing' (bajo cero), 'above freezing' (sobre cero). British: 'zero degrees', American also: 'zero degrees' or 'freezing point'."
        },
        {
          "type": "mc",
          "q": "In a chemistry lab, a student observes: 'The water is producing bubbles and turning into gas.' What process is happening?",
          "options": [
            "A) Freezing",
            "B) Melting",
            "C) Boiling",
            "D) Dissolving"
          ],
          "a": "C",
          "tip": "Look for the clue about bubbles and gas formation.",
          "successTip": "We say 'the water is boiling' (NOT 'the water boils' for action happening now). Expressions: 'bring to a boil' (calentar hasta hervir), 'come to a boil' (empezar a hervir), 'boiling hot' (muy caliente - informal)."
        },
        {
          "type": "input",
          "q": "Scientist: 'When you cool steam, it becomes liquid again. This process is called _____.' (changing from gas to liquid)",
          "a": "condensation",
          "alts": ["condensing"],
          "tip": "Think about what happens to water vapor when it cools.",
          "successTip": "Noun: 'condensation' (proceso). Verb: 'condense' (the steam condenses). Related: 'evaporation' (opposite process). Note: '-ation' ending creates nouns from verbs: evaporate → evaporation."
        },
        {
          "type": "input",
          "q": "Lab instructor: 'If you put sugar in water and stir, the sugar _____.' (disappears into the water)",
          "a": "dissolves",
          "alts": [],
          "tip": "The sugar seems to disappear but is still in the water.",
          "successTip": "Verb 'dissolve' (transitive or intransitive). We say 'dissolve IN water' (NOT 'into'). Phrasal verb: 'dissolve into' (cambiar a otro estado, como lágrimas). Noun: 'solution' (NOT 'dissolvement')."
        },
        {
          "type": "order",
          "q": "Chemist explains: 'Si congelas agua, se convierte en hielo.'",
          "words": ["If", "you", "freeze", "water,", "it", "becomes", "ice."],
          "a": "If you freeze water, it becomes ice.",
          "extra": ["steam", "boils"],
          "tip": "This is about changing water to solid form.",
          "successTip": "'Become' is followed by noun or adjective (NOT verb). Examples: 'become ice' (noun), 'become solid' (adjective), but NOT 'become freezing'. Alternative: 'turn into ice' (phrasal verb with same meaning)."
        },
        {
          "type": "order",
          "q": "Physics teacher: 'Cuando hierves agua, produces vapor.'",
          "words": ["When", "you", "boil", "water,", "you", "produce", "steam."],
          "a": "When you boil water, you produce steam.",
          "extra": ["ice", "crystals"],
          "tip": "Steam is water in gas form.",
          "successTip": "Verb 'produce' is formal/technical. In everyday speech, use 'make' or 'create'. Collocation: 'produce steam/energy/results' (formal contexts). Noun: 'production' (el proceso de producir)."
        },
        {
          "type": "order",
          "q": "Lab demonstration: 'Si calientas chocolate, se derrite.'",
          "words": ["If", "you", "heat", "chocolate,", "it", "melts."],
          "a": "If you heat chocolate, it melts.",
          "extra": ["freezes", "boils"],
          "tip": "Chocolate changes from solid to liquid when heated.",
          "successTip": "Verb 'heat' vs 'heat up': 'heat' (more formal/scientific), 'heat up' (phrasal verb - more informal). Both correct. We say 'heat water' (transitive) but 'the room heats up' (intransitive with 'up')."
        },
        {
          "type": "order",
          "q": "Science experiment: 'Cuando el agua se enfría a cero grados, se congela.'",
          "words": ["When", "water", "cools", "to", "zero", "degrees,", "it", "freezes."],
          "a": "When water cools to zero degrees, it freezes.",
          "extra": ["boils", "evaporates"],
          "tip": "Zero degrees Celsius is the freezing point.",
          "successTip": "Verb 'cool' can be transitive (cool something) or intransitive (something cools). Phrasal verb: 'cool down' (enfriarse gradualmente). We say 'cool TO a temperature' (NOT 'cool at')."
        }
      ]
    },
    {
      "set": 3,
      "title": "Chemical Reactions & Laboratory Actions",
      "context": "Laboratory procedures, chemical reactions, and scientific method actions in experimental settings",
      "questions": [
        {
          "type": "match",
          "q": "Match the laboratory action with its description.",
          "matches": [
            {"left": "To pour", "right": "Transfer liquid from one container to another"},
            {"left": "To stir", "right": "Mix substances by moving them around"},
            {"left": "To heat", "right": "Increase the temperature of something"},
            {"left": "To observe", "right": "Watch carefully and record what happens"}
          ],
          "tip": "These are common actions scientists perform in experiments.",
          "successTip": "Verb 'pour': always needs direction - 'pour INTO a beaker', 'pour OUT of a bottle'. Phrasal: 'pour out' (servir bebida). 'Stir' vs 'mix': stir uses circular motion, mix is more general."
        },
        {
          "type": "match",
          "q": "Match the term with its scientific meaning.",
          "matches": [
            {"left": "Chemicals", "right": "Substances used in chemistry experiments"},
            {"left": "Reaction", "right": "Process where substances change into new substances"},
            {"left": "To burn", "right": "Undergo combustion with fire"},
            {"left": "To record", "right": "Write down observations and data"}
          ],
          "tip": "These terms describe materials and processes in chemistry.",
          "successTip": "'Chemicals' is always plural when meaning 'substances'. 'Record' as verb is stressed on second syllable: re-CORD. As noun, first syllable: RE-cord. Phrasal verb: 'write down' = record (more informal)."
        },
        {
          "type": "mc",
          "q": "A lab instructor warns: 'If you mix these two chemicals without safety equipment, you could get hurt.' What should you wear?",
          "options": [
            "A) Regular clothes only",
            "B) Safety goggles and gloves",
            "C) A hat",
            "D) Sunglasses"
          ],
          "a": "B",
          "tip": "Think about what protects you from chemical splashes.",
          "successTip": "We say 'wear safety equipment' (NOT 'use'). Fixed expressions: 'put on gloves' (ponerse), 'take off gloves' (quitarse). 'Protective equipment' and 'safety equipment' are interchangeable."
        },
        {
          "type": "mc",
          "q": "A chemist explains: 'When carbon reacts with oxygen, it produces carbon dioxide.' What type of reaction is this?",
          "options": [
            "A) A physical change",
            "B) A combustion reaction",
            "C) Freezing",
            "D) Dissolving"
          ],
          "a": "B",
          "tip": "This reaction involves oxygen and produces a new substance.",
          "successTip": "Verb 'react': 'react WITH something' (NOT 'react to' in chemistry context). Noun: 'reaction'. Collocation: 'chemical reaction', 'violent reaction', 'chain reaction'. Adjective: 'reactive' (that reacts easily)."
        },
        {
          "type": "input",
          "q": "Professor: 'Before we start the experiment, we need to write down our _____. This is our prediction of what will happen.' (prediction)",
          "a": "hypothesis",
          "alts": ["prediction"],
          "tip": "This is the first step of the scientific method after asking a question.",
          "successTip": "Plural: 'hypotheses' (NOT 'hypothesis'). Verb: 'hypothesize' (to hypothesize that...). We say 'make/form/develop a hypothesis' (NOT 'do a hypothesis'). More formal than 'prediction'."
        },
        {
          "type": "order",
          "q": "Lab safety rule: 'Si tocas químicos, lávate las manos.'",
          "words": ["If", "you", "touch", "chemicals,", "wash", "your", "hands."],
          "a": "If you touch chemicals, wash your hands.",
          "extra": ["goggles", "experiment"],
          "tip": "This is an imperative with conditional structure for safety.",
          "successTip": "Imperative in conditionals: 'If you X, do Y' (instruction/advice). More polite: 'If you X, please do Y' or 'If you X, you should do Y'. Body parts with 'your': 'wash your hands' (NOT 'wash the hands')."
        },
        {
          "type": "order",
          "q": "Science observation: 'Cuando mezclas vinagre y bicarbonato, produces gas.'",
          "words": ["When", "you", "mix", "vinegar", "and", "baking", "soda,", "you", "produce", "gas."],
          "a": "When you mix vinegar and baking soda, you produce gas.",
          "extra": ["crystals", "ice"],
          "tip": "This is a common chemistry demonstration showing a reaction.",
          "successTip": "'Baking soda' (US English) = 'bicarbonate of soda' (UK English). Both correct. When mixing two things, use 'and' (NOT 'with'): 'mix A and B' (NOT 'mix A with B' - this means adding B to A)."
        },
        {
          "type": "order",
          "q": "Chemistry class: 'Si quemas papel, produces ceniza.'",
          "words": ["If", "you", "burn", "paper,", "it", "produces", "ash."],
          "a": "If you burn paper, it produces ash.",
          "extra": ["melts", "freezes"],
          "tip": "Burning is a chemical reaction that changes the substance.",
          "successTip": "Verb 'burn' is irregular: burn-burned/burnt-burned/burnt. American prefers 'burned', British often uses 'burnt'. Phrasal verbs: 'burn down' (destroy by fire), 'burn out' (extinguirse), 'burn up' (consumir completamente)."
        },
        {
          "type": "order",
          "q": "Laboratory instruction: 'Cuando viertes el líquido, hazlo lentamente.'",
          "words": ["When", "you", "pour", "the", "liquid,", "do", "it", "slowly."],
          "a": "When you pour the liquid, do it slowly.",
          "extra": ["quickly", "never"],
          "tip": "This combines a time clause with an imperative instruction.",
          "successTip": "'Do it' refers back to the action (pour). Common pattern for instructions: 'When you do X, do it + adverb'. Alternatives: 'pour it slowly' or 'pour slowly' (without 'do it')."
        },
        {
          "type": "mc",
          "q": "A researcher says: 'I need to record all the results from this experiment.' What does 'record' mean in this context?",
          "options": [
            "A) To play music",
            "B) To write down data",
            "C) To watch carefully",
            "D) To heat something"
          ],
          "a": "B",
          "tip": "Scientists need to keep track of their observations.",
          "successTip": "Pronunciation changes meaning! RE-cord (noun) = registro. re-CORD (verb) = registrar. Similar patterns: PER-mit (noun) / per-MIT (verb), PRE-sent (noun) / pre-SENT (verb)."
        }
      ]
    },
    {
      "set": 4,
      "title": "Magnets, Electricity & Physical Phenomena",
      "context": "Electromagnetic properties, electrical phenomena, and fundamental physics concepts in demonstrations",
      "questions": [
        {
          "type": "match",
          "q": "Match the electromagnetic term with its definition.",
          "matches": [
            {"left": "Magnet", "right": "Object that attracts iron and certain metals"},
            {"left": "Battery", "right": "Device that stores electrical energy"},
            {"left": "To attract", "right": "Pull objects toward something"},
            {"left": "To repel", "right": "Push objects away from something"}
          ],
          "tip": "These terms relate to magnetism and electricity.",
          "successTip": "'Attract' and 'repel' are opposites. Both transitive: 'magnets attract metal' (NOT 'attract to'). Noun: 'attraction' / 'repulsion'. Adjective: 'attractive' (also means 'good-looking'), 'repellent' (que repele)."
        },
        {
          "type": "match",
          "q": "Match the electrical term with its meaning.",
          "matches": [
            {"left": "Static electricity", "right": "Electrical charge that builds up on surfaces"},
            {"left": "Electric shock", "right": "Sudden discharge of electricity through the body"},
            {"left": "To float", "right": "Stay on the surface of a liquid"},
            {"left": "To sink", "right": "Go down below the surface of a liquid"}
          ],
          "tip": "Think about electrical phenomena and buoyancy.",
          "successTip": "'Static' (adjective) = no moving. Opposite of 'dynamic'. 'Electric' vs 'electrical': 'electric shock' (directo), 'electrical engineering' (relacionado con electricidad en general). Both adjectives but different usage."
        },
        {
          "type": "mc",
          "q": "A physics teacher demonstrates: 'When I rub this balloon on my hair, it creates static electricity. What happens if I bring the balloon near small paper pieces?'",
          "options": [
            "A) Nothing happens",
            "B) The paper pieces are attracted to the balloon",
            "C) The paper dissolves",
            "D) The paper burns"
          ],
          "a": "B",
          "tip": "Static electricity can attract lightweight objects.",
          "successTip": "We say 'rub something ON something' (NOT 'rub something with'). Phrasal verb: 'rub out' (borrar). 'Bring near' = 'move closer to' (NOT 'bring to near')."
        },
        {
          "type": "mc",
          "q": "An electrician explains: 'If you touch a live wire without insulation, you get an electric shock.' Why is this dangerous?",
          "options": [
            "A) It can disrupt your heart rhythm",
            "B) It makes you magnetic",
            "C) It changes your temperature",
            "D) It makes you float"
          ],
          "a": "A",
          "tip": "Electricity affects the body's electrical signals, especially in the heart.",
          "successTip": "'Get a shock' or 'receive a shock' (NOT 'take a shock'). Fixed expression: 'live wire' (cable con corriente). In British English, also 'be live' (estar con corriente). Opposites: 'dead wire' (sin corriente)."
        },
        {
          "type": "input",
          "q": "Science teacher: 'When you put a magnet near a nail, the magnet _____ the nail.' (pulls toward)",
          "a": "attracts",
          "alts": ["pulls"],
          "tip": "Magnets pull magnetic materials toward them.",
          "successTip": "Pattern: 'attract + object' (transitive verb, no preposition). WRONG: 'attract to'. Compare: 'be attracted to' (passive form, needs 'to'). Example: 'The nail is attracted to the magnet' (passive)."
        },
        {
          "type": "input",
          "q": "Lab demonstration: 'If you put two magnets with the same pole together, they _____ each other.' (push away)",
          "a": "repel",
          "alts": ["push"],
          "tip": "Same poles do not want to come together.",
          "successTip": "'Repel each other' (reciprocal action). Other reciprocal verbs: 'attract each other', 'touch each other', 'see each other'. Note: 'repel' also means 'repugnar' in different context."
        },
        {
          "type": "order",
          "q": "Physics principle: 'Si sueltas una pelota, rebota.'",
          "words": ["If", "you", "drop", "a", "ball,", "it", "bounces."],
          "a": "If you drop a ball, it bounces.",
          "extra": ["floats", "melts"],
          "tip": "This describes elastic objects hitting a surface.",
          "successTip": "'Bounce' can be intransitive (the ball bounces) or transitive (bounce a ball). Phrasal verb: 'bounce back' (recuperarse). Expression: 'bounce ideas off someone' (discutir ideas informalmente)."
        },
        {
          "type": "order",
          "q": "Electricity safety: 'Si tocas un cable sin aislamiento, recibes una descarga.'",
          "words": ["If", "you", "touch", "a", "live", "wire,", "you", "get", "an", "electric", "shock."],
          "a": "If you touch a live wire, you get an electric shock.",
          "extra": ["magnet", "battery"],
          "tip": "This is a warning about electrical danger.",
          "successTip": "'Get a shock' (NOT 'receive' or 'take' in informal speech). Adjective 'live' /laɪv/ (con corriente) vs 'live' /lɪv/ (en vivo). Different pronunciation! 'Live wire' = both meanings possible depending on context."
        },
        {
          "type": "order",
          "q": "Science demonstration: 'Cuando pones un imán cerca de hierro, lo atrae.'",
          "words": ["When", "you", "put", "a", "magnet", "near", "iron,", "it", "attracts", "it."],
          "a": "When you put a magnet near iron, it attracts it.",
          "extra": ["repels", "melts"],
          "tip": "Iron is strongly magnetic and responds to magnets.",
          "successTip": "Double 'it' pattern: first 'it' = subject (magnet), second 'it' = object (iron). Common in English. Alternative: 'the magnet attracts it' (clearer subject). Preposition: 'near' (NOT 'near to')."
        },
        {
          "type": "order",
          "q": "Buoyancy experiment: 'Si pones corcho en agua, flota.'",
          "words": ["If", "you", "put", "cork", "in", "water,", "it", "floats."],
          "a": "If you put cork in water, it floats.",
          "extra": ["sinks", "burns"],
          "tip": "Cork is less dense than water.",
          "successTip": "'Cork' is both countable (a cork = tapón) and uncountable (cork = material). Pattern: 'put something IN liquid' (within the liquid) vs 'put something ON liquid' (superficie). Different meanings!"
        }
      ]
    },
    {
      "set": 5,
      "title": "Scientific Method & Experimentation",
      "context": "Steps of scientific method, experimental design, and research procedures in scientific investigation",
      "questions": [
        {
          "type": "match",
          "q": "Match the step of the scientific method with its description.",
          "matches": [
            {"left": "Observation", "right": "Noticing something interesting in nature"},
            {"left": "Hypothesis", "right": "Making a testable prediction"},
            {"left": "Experiment", "right": "Testing the hypothesis with controlled conditions"},
            {"left": "Conclusion", "right": "Determining if results support the hypothesis"}
          ],
          "tip": "These are the main steps scientists follow to investigate questions.",
          "successTip": "These nouns form verbs: 'observe' (verb), 'hypothesize' (verb), 'experiment' (verb - less common), 'conclude' (verb). Pattern: 'make an observation', 'form a hypothesis', 'conduct an experiment', 'draw a conclusion'."
        },
        {
          "type": "match",
          "q": "Match the laboratory tool with its specific use.",
          "matches": [
            {"left": "Beaker", "right": "Holds and heats liquids"},
            {"left": "Test tube", "right": "Holds small amounts of chemicals for reactions"},
            {"left": "Bunsen burner", "right": "Provides controlled heat source"},
            {"left": "Pipette", "right": "Transfers precise volumes of liquid"}
          ],
          "tip": "Each lab tool has a specific purpose for precision and safety.",
          "successTip": "'Test tube' (two words). Pronunciation: BUN-sen burner (not 'bunzen'). We say 'use a beaker' or 'pour into a beaker' (NOT 'work with beaker'). Collocations with tools use 'use' or specific action verbs."
        },
        {
          "type": "mc",
          "q": "A researcher states: 'First, we observe the problem. Then we make a hypothesis. What comes next in the scientific method?'",
          "options": [
            "A) Write a conclusion",
            "B) Design and conduct an experiment",
            "C) Publish the results",
            "D) Stop the research"
          ],
          "a": "B",
          "tip": "After predicting, scientists need to test their prediction.",
          "successTip": "Verb 'conduct' = llevar a cabo (formal). Alternatives: 'carry out', 'perform', 'do' (informal). We say 'conduct an experiment/survey/research' (NOT 'make'). 'Conduct' also means 'dirigir' (orchestra)."
        },
        {
          "type": "mc",
          "q": "A biology teacher explains: 'If your experiment doesn't support your hypothesis, what should you do?'",
          "options": [
            "A) Hide the results",
            "B) Change the results to fit the hypothesis",
            "C) Revise the hypothesis and test again",
            "D) Stop doing science"
          ],
          "a": "C",
          "tip": "Negative results are still valuable in science.",
          "successTip": "'Support' as verb (NOT 'supports to'). Phrasal verb: 'back up' = support (informal). We say 'support a hypothesis/theory/claim' (NOT 'support to'). Pattern: verb + object directly."
        },
        {
          "type": "input",
          "q": "Lab instructor: 'At the end of the experiment, we analyze the _____ to see what we learned.' (data collected)",
          "a": "results",
          "alts": ["data", "findings"],
          "tip": "This is what you collect and record during an experiment.",
          "successTip": "'Results' (always plural in this context). Verb: 'analyze results' or 'examine results'. 'Data' can be singular (UK: singular, US: often plural). Formal: 'findings' (hallazgos). Collocation: 'publish/report/analyze results'."
        },
        {
          "type": "order",
          "q": "Scientific process: 'Si el experimento falla, aprendes algo nuevo.'",
          "words": ["If", "the", "experiment", "fails,", "you", "learn", "something", "new."],
          "a": "If the experiment fails, you learn something new.",
          "extra": ["stop", "results"],
          "tip": "Failure in science is actually valuable information.",
          "successTip": "'Fail' (verb) vs 'failure' (noun). Phrasal verbs: 'fail to do something' (no lograr), NOT 'fail doing'. We say 'learn something' (NOT 'learn anything' in positive statements). Use 'anything' in questions/negatives."
        },
        {
          "type": "order",
          "q": "Research principle: 'Cuando registras tus observaciones, obtienes datos confiables.'",
          "words": ["When", "you", "record", "your", "observations,", "you", "get", "reliable", "data."],
          "a": "When you record your observations, you get reliable data.",
          "extra": ["hypothesis", "fails"],
          "tip": "Recording observations immediately ensures accuracy.",
          "successTip": "Adjective 'reliable' (confiable) vs 'reliant' (dependiente). Pattern: 'reliable + noun' (reliable data/source/person). Noun: 'reliability'. Verb: 'rely on' (depender de). Don't confuse!"
        },
        {
          "type": "order",
          "q": "Experimental design: 'Si controlas las variables, obtienes resultados precisos.'",
          "words": ["If", "you", "control", "the", "variables,", "you", "get", "accurate", "results."],
          "a": "If you control the variables, you get accurate results.",
          "extra": ["hypothesis", "observation"],
          "tip": "Controlling variables means only changing one thing at a time.",
          "successTip": "'Accurate' (preciso) vs 'precise' (exacto). Similar but different! 'Control variables' = manage/regulate. Don't confuse with 'check variables' (verificar). Phrasal verb: 'control for' (compensar por) in statistics."
        },
        {
          "type": "order",
          "q": "Laboratory safety: 'Cuando trabajas con químicos, usas equipo de protección.'",
          "words": ["When", "you", "work", "with", "chemicals,", "you", "use", "protective", "equipment."],
          "a": "When you work with chemicals, you use protective equipment.",
          "extra": ["hypothesis", "observation"],
          "tip": "Safety equipment protects you from dangerous substances.",
          "successTip": "'Equipment' is ALWAYS singular (even though it feels plural). WRONG: 'equipments'. We say 'a piece of equipment' for one item. Adjective placement: 'protective equipment' (NOT 'equipment protective')."
        },
        {
          "type": "input",
          "q": "Scientist: 'Before we test anything, we need to make a testable _____.' (prediction about the outcome)",
          "a": "hypothesis",
          "alts": ["prediction"],
          "tip": "This educated guess guides the entire experiment.",
          "successTip": "Verb collocations: 'make/form/develop/propose a hypothesis' (NOT 'do'). Adjective 'testable' = that can be tested. Pattern: adjective + noun. Related: 'verifiable' (verificable), 'measurable' (medible), 'observable' (observable)."
        }
      ]
    },
    {
      "set": 6,
      "title": "Temperature, Heat & Thermal Changes",
      "context": "Temperature measurement, thermal processes, and heat transfer in scientific contexts",
      "questions": [
        {
          "type": "match",
          "q": "Match the temperature-related term with its definition.",
          "matches": [
            {"left": "Thermometer", "right": "Instrument that measures temperature"},
            {"left": "To cool", "right": "Lower the temperature of something"},
            {"left": "To heat", "right": "Raise the temperature of something"},
            {"left": "Steam", "right": "Water vapor produced by boiling"}
          ],
          "tip": "These terms all relate to temperature and thermal changes.",
          "successTip": "'Cool' can be adjective (fresco), verb (enfriar), or slang (genial). Context matters! Phrasal verbs: 'cool down' (enfriarse), 'heat up' (calentarse). We say 'turn up the heat' (subir temperatura), 'turn down the heat' (bajar)."
        },
        {
          "type": "match",
          "q": "Match the thermal process with what happens.",
          "matches": [
            {"left": "Boiling point", "right": "Temperature at which liquid becomes gas"},
            {"left": "Freezing point", "right": "Temperature at which liquid becomes solid"},
            {"left": "Melting point", "right": "Temperature at which solid becomes liquid"},
            {"left": "Evaporation", "right": "Gradual change from liquid to gas"}
          ],
          "tip": "These are specific temperatures where phase changes occur.",
          "successTip": "Compound nouns: 'boiling point' (two words), 'freezing point', 'melting point'. All use 'point' (NOT 'temperature'). We say 'AT the boiling point' (NOT 'in'). Related: 'breaking point' (límite emocional)."
        },
        {
          "type": "mc",
          "q": "A physics teacher asks: 'If you heat water to 100 degrees Celsius at sea level, what happens?'",
          "options": [
            "A) It freezes",
            "B) It boils",
            "C) Nothing happens",
            "D) It turns to ice"
          ],
          "a": "B",
          "tip": "100°C is a significant temperature for water.",
          "successTip": "Temperature expressions: 'at 100 degrees' (NOT 'in'), 'to 100 degrees' (hasta). Pronunciation: 'Celsius' /ˈselsiəs/. American English often uses 'Fahrenheit' instead. Pattern: number + degrees + scale name."
        },
        {
          "type": "mc",
          "q": "A chemist explains: 'When you remove heat from water and it reaches 0°C, it undergoes a phase change.' What is this change called?",
          "options": [
            "A) Evaporation",
            "B) Condensation",
            "C) Freezing",
            "D) Boiling"
          ],
          "a": "C",
          "tip": "Removing heat causes water to become solid.",
          "successTip": "'Undergo' = experimentar (formal verb for processes). We say 'undergo a change/process/transformation' (NOT 'suffer'). Alternative: 'go through' (phrasal verb, less formal). Pronunciation: /ˌʌndərˈɡoʊ/."
        },
        {
          "type": "input",
          "q": "Lab instructor: 'When you add heat to ice, it changes from solid to liquid. This process is called _____.' (solid to liquid)",
          "a": "melting",
          "alts": ["thawing"],
          "tip": "Adding heat causes ice to become water.",
          "successTip": "'Melting' (general term) vs 'thawing' (specifically for frozen food/ice melting naturally). Both correct but different usage. Adjective: 'molten' (derretido) for metals (molten lava/metal), NOT for ice."
        },
        {
          "type": "input",
          "q": "Science teacher: 'The instrument we use to measure how hot or cold something is called a _____.'",
          "a": "thermometer",
          "alts": [],
          "tip": "'Thermo' means heat or temperature.",
          "successTip": "Greek prefix 'thermo-' (calor): thermometer, thermostat, thermal, thermos. Pattern: 'use a thermometer' (NOT 'use thermometer' - needs article). We say 'take the temperature' WITH a thermometer."
        },
        {
          "type": "order",
          "q": "Scientific fact: 'Si calientas hielo, se derrite en agua.'",
          "words": ["If", "you", "heat", "ice,", "it", "melts", "into", "water."],
          "a": "If you heat ice, it melts into water.",
          "extra": ["boils", "freezes"],
          "tip": "Adding heat to solid water causes a phase change.",
          "successTip": "'Melt INTO' shows transformation result. Other patterns: 'change into', 'turn into', 'transform into'. Note preposition 'into' (NOT 'to'). Compare: 'melt' (intransitive) vs 'melt something' (transitive)."
        },
        {
          "type": "order",
          "q": "Thermal physics: 'Cuando el agua llega a cien grados, hierve.'",
          "words": ["When", "water", "reaches", "one", "hundred", "degrees,", "it", "boils."],
          "a": "When water reaches one hundred degrees, it boils.",
          "extra": ["freezes", "melts"],
          "tip": "100°C is water's boiling point at normal pressure.",
          "successTip": "'Reach' a temperature (NOT 'arrive to'). Common mistake! Verb 'reach' is transitive: reach something (NO preposition). We write numbers: '100' or 'one hundred' (both OK). Formal writing prefers digits for scientific contexts."
        },
        {
          "type": "order",
          "q": "Kitchen science: 'Si pones agua en el congelador, se convierte en hielo.'",
          "words": ["If", "you", "put", "water", "in", "the", "freezer,", "it", "becomes", "ice."],
          "a": "If you put water in the freezer, it becomes ice.",
          "extra": ["steam", "gas"],
          "tip": "The freezer removes heat, causing water to freeze.",
          "successTip": "'Freezer' (aparato) vs 'freeze' (verbo). Pattern: verb + -er = machine/person (freeze → freezer, mix → mixer). We say 'IN the freezer' (NOT 'into' for location). Use 'into' only for movement direction."
        },
        {
          "type": "order",
          "q": "Phase change: 'Cuando el vapor se enfría, se condensa en agua.'",
          "words": ["When", "steam", "cools,", "it", "condenses", "into", "water."],
          "a": "When steam cools, it condenses into water.",
          "extra": ["freezes", "melts"],
          "tip": "Cooling gas turns it back into liquid.",
          "successTip": "'Condense INTO' (result of transformation). Verb 'condense' also means 'summarize' (different meaning!). Noun: 'condensation' (proceso). We say 'steam' (water vapor) vs 'smoke' (combustion particles) - different things!"
        }
      ]
    },
    {
      "set": 7,
      "title": "Density, Buoyancy & Physical Properties",
      "context": "Physical properties of matter, density principles, and buoyancy in fluids",
      "questions": [
        {
          "type": "match",
          "q": "Match the physical property with its definition.",
          "matches": [
            {"left": "Density", "right": "How much mass is in a given volume"},
            {"left": "Buoyancy", "right": "The upward force that liquids exert on objects"},
            {"left": "Mass", "right": "The amount of matter in an object"},
            {"left": "Volume", "right": "The amount of space an object occupies"}
          ],
          "tip": "These properties determine whether objects float or sink.",
          "successTip": "'Mass' (scientific - doesn't change) vs 'weight' (force due to gravity - changes). Uncountable nouns: 'mass', 'density', 'buoyancy' (no plural). 'Volume' can mean cantidad (uncountable) or volumen de sonido (countable)."
        },
        {
          "type": "match",
          "q": "Match the object with its behavior in water.",
          "matches": [
            {"left": "Rock", "right": "Sinks because it's denser than water"},
            {"left": "Wood", "right": "Usually floats because it's less dense"},
            {"left": "Oil", "right": "Floats on water surface"},
            {"left": "Iron nail", "right": "Sinks due to high density"}
          ],
          "tip": "Think about density compared to water.",
          "successTip": "Comparative structures: 'denser THAN' (NOT 'denser that'), 'less dense THAN'. Superlatives: 'the densest material', 'the least dense'. Pattern: more/less + adjective + than (for long adjectives)."
        },
        {
          "type": "mc",
          "q": "A physics teacher demonstrates: 'I drop a stone in water and it sinks. Then I drop a piece of wood and it floats. Why?'",
          "options": [
            "A) The stone is smaller",
            "B) Wood is magical",
            "C) The stone is denser than water",
            "D) Water doesn't like stones"
          ],
          "a": "C",
          "tip": "Think about the relationship between density and floating.",
          "successTip": "Comparative: 'denser than' (más denso que). Pattern: adjective-ER + than (short adjectives), more + adjective + than (long adjectives). Examples: bigger than, more expensive than. 'Dense' uses -er form."
        },
        {
          "type": "mc",
          "q": "A science student observes: 'When I pour oil into water, it stays on top and doesn't mix.' What explains this?",
          "options": [
            "A) Oil is less dense and hydrophobic",
            "B) Oil is heavier than water",
            "C) Water pushes oil up",
            "D) Oil dissolves in air"
          ],
          "a": "A",
          "tip": "Oil has lower density and doesn't mix with water molecules.",
          "successTip": "'Hydrophobic' (repele agua) vs 'hydrophilic' (atrae agua). Greek roots: 'hydro' (water) + 'phobic' (fearing). Similar: 'hydro-electric', 'dehydrated', 'hydraulic'. Academic vocabulary - know these prefixes!"
        },
        {
          "type": "input",
          "q": "Physicist: 'An object will _____ in water if it is less dense than water.' (stay on surface)",
          "a": "float",
          "alts": [],
          "tip": "This is what boats and wood do in water.",
          "successTip": "'Float' (intransitive verb - no object). Pattern: 'float ON water' (surface) or 'float IN water' (dentro del líquido). Phrasal verb: 'float around' (circular sin propósito). Noun: 'floatation device' (flotador)."
        },
        {
          "type": "input",
          "q": "Lab observation: 'When we drop a metal ball in water, it goes down to the bottom. We say it _____.' (goes below surface)",
          "a": "sinks",
          "alts": [],
          "tip": "Heavy, dense objects do this in water.",
          "successTip": "'Sink' is irregular: sink-sank-sunk. Phrasal verbs: 'sink in' (comprender gradualmente), 'sink down' (hundirse). Expression: 'sink or swim' (sobrevivir o fracasar). Also noun: kitchen sink (fregadero)."
        },
        {
          "type": "order",
          "q": "Density principle: 'Si un objeto es menos denso que el agua, flota.'",
          "words": ["If", "an", "object", "is", "less", "dense", "than", "water,", "it", "floats."],
          "a": "If an object is less dense than water, it floats.",
          "extra": ["sinks", "burns"],
          "tip": "Density comparison determines floating or sinking.",
          "successTip": "Comparative with 'less': 'less dense than', 'less expensive than', 'less difficult than'. Use 'less' for positive adjectives (NOT 'more not dense'). Pattern: less + adjective + than."
        },
        {
          "type": "order",
          "q": "Buoyancy experiment: 'Cuando pones una piedra en agua, se hunde.'",
          "words": ["When", "you", "put", "a", "rock", "in", "water,", "it", "sinks."],
          "a": "When you put a rock in water, it sinks.",
          "extra": ["floats", "melts"],
          "tip": "Rocks are denser than water.",
          "successTip": "'Put something IN water' (place inside liquid). Phrasal verbs with 'put': 'put in' (insertar), 'put on' (ponerse ropa), 'put out' (apagar), 'put up' (colgar), 'put off' (posponer). Very common verbs!"
        },
        {
          "type": "order",
          "q": "Physical science: 'Si pones aceite en agua, flota en la superficie.'",
          "words": ["If", "you", "put", "oil", "in", "water,", "it", "floats", "on", "the", "surface."],
          "a": "If you put oil in water, it floats on the surface.",
          "extra": ["sinks", "dissolves"],
          "tip": "Oil is lighter and doesn't mix with water.",
          "successTip": "'Float ON the surface' (NOT 'at the surface'). Fixed expression. Prepositions with surfaces: 'on the surface' (sobre), 'below the surface' (debajo), 'beneath the surface' (bajo). Metaphor: 'scratch the surface' (superficial understanding)."
        },
        {
          "type": "order",
          "q": "Classroom demonstration: 'Cuando pones madera en agua, generalmente flota.'",
          "words": ["When", "you", "put", "wood", "in", "water,", "it", "usually", "floats."],
          "a": "When you put wood in water, it usually floats.",
          "extra": ["sinks", "melts"],
          "tip": "Most types of wood are less dense than water.",
          "successTip": "Adverb placement: 'usually' goes before main verb (usually floats) or after 'be' verb (is usually). Other frequency adverbs: always, often, sometimes, rarely, never. Learn these positions - very important for natural English!"
        }
      ]
    },
    {
      "set": 8,
      "title": "Color Mixing & Light Properties",
      "context": "Color theory, light properties, and optical phenomena in scientific demonstrations",
      "questions": [
        {
          "type": "match",
          "q": "Match the color combination with its result.",
          "matches": [
            {"left": "Red + Yellow", "right": "Makes orange"},
            {"left": "Blue + Yellow", "right": "Makes green"},
            {"left": "Red + Blue", "right": "Makes purple"},
            {"left": "Red + White", "right": "Makes pink"}
          ],
          "tip": "These are basic color mixing principles.",
          "successTip": "Verb 'make' (crear resultado). We say 'make orange/green/purple' (NOT 'do'). Pattern: Color A + Color B + makes + Color C. Use 'and' between colors: 'red and yellow' (NOT 'red with yellow')."
        },
        {
          "type": "match",
          "q": "Match the light property with its description.",
          "matches": [
            {"left": "Reflection", "right": "Light bounces off a surface"},
            {"left": "Refraction", "right": "Light bends when passing through materials"},
            {"left": "Absorption", "right": "Material takes in light energy"},
            {"left": "Transparent", "right": "Allows light to pass through clearly"}
          ],
          "tip": "These describe how light interacts with materials.",
          "successTip": "Related verbs: 'reflect' (reflejar), 'refract' (refractar), 'absorb' (absorber). Phrasal verb: 'bounce off' = reflect (informal). Adjectives: transparent (transparente), opaque (opaco), translucent (translúcido). Learn the adjective forms!"
        },
        {
          "type": "mc",
          "q": "An art teacher explains: 'If you mix yellow paint and blue paint together, what color do you get?'",
          "options": [
            "A) Orange",
            "B) Purple",
            "C) Green",
            "D) Brown"
          ],
          "a": "C",
          "tip": "Think about grass and leaves—what color are they?",
          "successTip": "'Mix together' (phrasal verb - emphasis on combining). Pattern: 'mix A and B together' or just 'mix A and B'. Preposition: 'mix WITH' means add to (mix milk with coffee), 'mix AND' means combine equally."
        },
        {
          "type": "mc",
          "q": "A physics teacher demonstrates with a prism: 'When white light passes through a prism, it separates. What do we see?'",
          "options": [
            "A) Nothing—light disappears",
            "B) Only red and blue",
            "C) A rainbow of colors (spectrum)",
            "D) Everything becomes white"
          ],
          "a": "C",
          "tip": "Think about rainbows after rain.",
          "successTip": "'Pass through' (phrasal verb - atravesar). Pattern: 'light passes THROUGH glass' (NOT 'light passes glass'). Preposition essential! Noun: 'prism' /ˈprɪzəm/. Related: 'spectrum' (plural: spectra or spectrums)."
        },
        {
          "type": "input",
          "q": "Science teacher: 'When you combine red and yellow paint, you create _____.' (warm secondary color)",
          "a": "orange",
          "alts": [],
          "tip": "Think about the color of oranges and pumpkins.",
          "successTip": "'Orange' is both noun (the fruit) and adjective (the color). No plural for color: 'I like orange' (NOT 'oranges' when talking about color). Pattern: 'light orange', 'dark orange', 'bright orange' (adjective + color)."
        },
        {
          "type": "input",
          "q": "Artist explains: 'If you want to make purple, mix red and _____.' (primary color)",
          "a": "blue",
          "alts": [],
          "tip": "This primary color is the color of the sky.",
          "successTip": "Color adjectives don't have plural: 'blue eyes' (NOT 'blues eyes'). Idioms with 'blue': 'feeling blue' (triste), 'out of the blue' (inesperadamente), 'once in a blue moon' (muy raramente). Common color in idioms!"
        },
        {
          "type": "order",
          "q": "Color theory: 'Si mezclas rojo y amarillo, obtienes naranja.'",
          "words": ["If", "you", "mix", "red", "and", "yellow,", "you", "get", "orange."],
          "a": "If you mix red and yellow, you get orange.",
          "extra": ["green", "purple"],
          "tip": "Two warm primary colors make this secondary color.",
          "successTip": "'Get' as verb has many meanings. Here = obtain/receive. Other meanings: 'get tired' (become), 'get home' (arrive), 'get a joke' (understand), 'get angry' (become). Very versatile verb - essential to master!"
        },
        {
          "type": "order",
          "q": "Paint mixing: 'Cuando combinas azul y amarillo, produces verde.'",
          "words": ["When", "you", "combine", "blue", "and", "yellow,", "you", "produce", "green."],
          "a": "When you combine blue and yellow, you produce green.",
          "extra": ["orange", "purple"],
          "tip": "This is the color of plants and grass.",
          "successTip": "'Combine' (formal) = 'mix' (common). Both correct. Pattern: 'combine A with B' (add B to A) vs 'combine A and B' (equal mixing). Subtle difference! Related: 'combination' (noun)."
        },
        {
          "type": "order",
          "q": "Light physics: 'Si la luz pasa por un prisma, se separa en colores.'",
          "words": ["If", "light", "passes", "through", "a", "prism,", "it", "separates", "into", "colors."],
          "a": "If light passes through a prism, it separates into colors.",
          "extra": ["mixes", "disappears"],
          "tip": "This creates a rainbow effect called a spectrum.",
          "successTip": "'Separate INTO' (divide and result). Pattern: separate into parts/groups/colors. Compare: 'separate FROM' (move apart from something). Different prepositions = different meanings! Learn preposition patterns with verbs."
        },
        {
          "type": "order",
          "q": "Art class: 'Cuando mezclas rojo y azul, haces púrpura.'",
          "words": ["When", "you", "mix", "red", "and", "blue,", "you", "make", "purple."],
          "a": "When you mix red and blue, you make purple.",
          "extra": ["orange", "green"],
          "tip": "This secondary color is made from a warm and cool primary.",
          "successTip": "'Make' vs 'do': 'make' creates something physical/result (make a cake, make purple). 'Do' performs action/activity (do homework, do exercise). Common error for Spanish speakers - study this carefully!"
        }
      ]
    },
    {
      "set": 9,
      "title": "Gravity, Motion & Basic Physics",
      "context": "Fundamental forces, motion principles, and basic physics concepts in demonstrations",
      "questions": [
        {
          "type": "match",
          "q": "Match the physics term with its definition.",
          "matches": [
            {"left": "Gravity", "right": "Force that pulls objects toward Earth"},
            {"left": "Friction", "right": "Force that opposes motion between surfaces"},
            {"left": "Momentum", "right": "Force of a moving object"},
            {"left": "Inertia", "right": "Tendency of objects to resist changes in motion"}
          ],
          "tip": "These are fundamental forces and properties in physics.",
          "successTip": "Abstract nouns (uncountable): 'gravity', 'friction', 'momentum', 'inertia' (no plural forms). Adjectives: 'gravitational' (force), 'frictional' (resistance). Pattern: noun form vs adjective form - learn both!"
        },
        {
          "type": "match",
          "q": "Match the motion term with its meaning.",
          "matches": [
            {"left": "To fall", "right": "Move downward due to gravity"},
            {"left": "To bounce", "right": "Spring back after hitting a surface"},
            {"left": "To roll", "right": "Move by turning over and over"},
            {"left": "To slide", "right": "Move smoothly across a surface"}
          ],
          "tip": "These describe different types of motion.",
          "successTip": "Motion verbs: 'fall' (irregular: fall-fell-fallen), 'bounce' (regular), 'roll' (regular), 'slide' (irregular: slide-slid-slid). Phrasal verbs: 'fall down' (caerse), 'fall over' (volcarse), 'roll over' (dar vuelta). Learn irregular forms!"
        },
        {
          "type": "mc",
          "q": "A physics teacher asks: 'If you drop a ball and a feather from the same height on Earth, which hits the ground first?'",
          "options": [
            "A) The feather, because it's lighter",
            "B) The ball, because air resistance affects the feather more",
            "C) They hit at the same time",
            "D) Neither falls"
          ],
          "a": "B",
          "tip": "Consider the effect of air pushing against objects.",
          "successTip": "'Drop' (dejar caer - transitive) vs 'fall' (caer - intransitive). Pattern: 'drop something' (you control action), 'something falls' (gravity controls). Common mistake: 'fall something' is WRONG!"
        },
        {
          "type": "mc",
          "q": "A teacher demonstrates: 'I push this book across the table and it slows down and stops. What force causes this?'",
          "options": [
            "A) Gravity",
            "B) Magnetism",
            "C) Friction",
            "D) Electricity"
          ],
          "a": "C",
          "tip": "This force opposes motion between touching surfaces.",
          "successTip": "'Slow down' (phrasal verb - reducir velocidad). Opposite: 'speed up' (acelerar). Pattern: verb + particle changes meaning. Single word 'slow' (adjective/verb) vs phrasal 'slow down' (reduce speed gradually)."
        },
        {
          "type": "input",
          "q": "Science teacher: 'When you throw a ball up, it comes back down because of _____.' (force pulling toward Earth)",
          "a": "gravity",
          "alts": [],
          "tip": "This force keeps us on the ground and makes objects fall.",
          "successTip": "Uncountable noun: 'gravity' (no plural). Related: 'gravitation' (formal/scientific), 'gravitational pull' (collocation). Idiom: 'the gravity of the situation' (seriousness - different meaning!). Context determines meaning."
        },
        {
          "type": "input",
          "q": "Physics experiment: 'When you drop a rubber ball, it hits the ground and springs back up. We say it _____.' (springs back)",
          "a": "bounces",
          "alts": [],
          "tip": "Elastic objects do this when they hit surfaces.",
          "successTip": "'Bounce' can be transitive (bounce a ball) or intransitive (the ball bounces). Phrasal verb: 'bounce back' (recover - metaphorical). Expression: 'bounce ideas off someone' (discuss informally). Multiple meanings!"
        },
        {
          "type": "order",
          "q": "Newton's law: 'Si sueltas un objeto, cae al suelo.'",
          "words": ["If", "you", "drop", "an", "object,", "it", "falls", "to", "the", "ground."],
          "a": "If you drop an object, it falls to the ground.",
          "extra": ["floats", "rises"],
          "tip": "Gravity pulls all objects downward.",
          "successTip": "'To the ground' (direction toward) vs 'on the ground' (location/position). Prepositions matter! 'To' shows movement direction, 'on' shows static location. Common pattern in English."
        },
        {
          "type": "order",
          "q": "Motion principle: 'Cuando lanzas una pelota hacia arriba, regresa hacia abajo.'",
          "words": ["When", "you", "throw", "a", "ball", "up,", "it", "comes", "back", "down."],
          "a": "When you throw a ball up, it comes back down.",
          "extra": ["stays", "disappears"],
          "tip": "Gravity always pulls objects back to Earth.",
          "successTip": "'Come back' (phrasal verb - regresar). 'Up' and 'down' as adverb particles (NOT prepositions here). Pattern: verb + particle. Compare: 'throw the ball up' vs 'throw up' (vomitar - completely different meaning!). Context essential."
        },
        {
          "type": "order",
          "q": "Friction experiment: 'Si empujas un objeto en una superficie lisa, se desliza.'",
          "words": ["If", "you", "push", "an", "object", "on", "a", "smooth", "surface,", "it", "slides."],
          "a": "If you push an object on a smooth surface, it slides.",
          "extra": ["bounces", "floats"],
          "tip": "Smooth surfaces have less friction, allowing easier movement.",
          "successTip": "'Smooth' (liso) vs 'soft' (suave al tacto). Different meanings! 'Smooth surface' (no friction), 'soft pillow' (comfortable). Common confusion. Related: 'smoothly' (adverb - sin problemas). Learn adjective vs adverb!"
        },
        {
          "type": "order",
          "q": "Elasticity demonstration: 'Cuando tiras de una banda elástica, regresa a su forma original.'",
          "words": ["When", "you", "stretch", "a", "rubber", "band,", "it", "returns", "to", "its", "original", "shape."],
          "a": "When you stretch a rubber band, it returns to its original shape.",
          "extra": ["breaks", "melts"],
          "tip": "Elastic materials return to their shape after being stretched.",
          "successTip": "'Return TO' (NOT 'return at'). Pattern: return to + place/state. Phrasal verb: 'go back to' (same meaning, more informal). 'Original' as adjective goes before noun: 'original shape' (NOT 'shape original')."
        }
      ]
    },
    {
      "set": 10,
      "title": "Dissolving, Solutions & Chemical Mixtures",
      "context": "Solution chemistry, dissolving processes, and mixture formation in chemical contexts",
      "questions": [
        {
          "type": "match",
          "q": "Match the solution term with its definition.",
          "matches": [
            {"left": "Solution", "right": "Mixture where one substance dissolves in another"},
            {"left": "Solvent", "right": "The liquid that dissolves other substances"},
            {"left": "Solute", "right": "The substance that dissolves in liquid"},
            {"left": "Saturated", "right": "When no more solute can dissolve"}
          ],
          "tip": "These are key concepts in understanding mixtures and solutions.",
          "successTip": "'Solution' has two meanings: 1) chemical mixture (química), 2) answer to problem (respuesta). Context determines meaning! Related words: 'solve' (verb - resolver), 'soluble' (adjective - que se disuelve)."
        },
        {
          "type": "match",
          "q": "Match the mixing term with its description.",
          "matches": [
            {"left": "To dissolve", "right": "Substance disappears into liquid"},
            {"left": "To mix", "right": "Combine two or more substances"},
            {"left": "To stir", "right": "Move liquid around to help dissolving"},
            {"left": "To separate", "right": "Divide mixed substances apart"}
          ],
          "tip": "These actions are common in chemistry experiments.",
          "successTip": "Verb patterns: 'dissolve IN water' (NOT 'into'), 'mix A with B' or 'mix A and B', 'stir the mixture' (transitive), 'separate FROM' (dividir de). Prepositions crucial for meaning!"
        },
        {
          "type": "mc",
          "q": "A chemistry teacher demonstrates: 'I put sugar in water and stir. The sugar disappears but the water tastes sweet. What happened?'",
          "options": [
            "A) The sugar evaporated",
            "B) The sugar dissolved in the water",
            "C) The sugar turned into water",
            "D) The sugar burned"
          ],
          "a": "B",
          "tip": "The sugar is still there, just not visible.",
          "successTip": "'Dissolve IN' (NOT 'dissolve into'). Fixed preposition pattern. Exception: 'dissolve into tears' (different meaning - romper en llanto). Learn fixed expressions separately from literal meanings!"
        },
        {
          "type": "mc",
          "q": "A student asks: 'I put salt in cold water and it dissolves slowly. How can I make it dissolve faster?'",
          "options": [
            "A) Add more salt",
            "B) Heat the water and stir",
            "C) Let it sit for days",
            "D) Put it in the freezer"
          ],
          "a": "B",
          "tip": "Think about what makes molecules move faster.",
          "successTip": "'Make something dissolve faster' (causative pattern). Structure: 'make + object + base verb' (NOT infinitive). Examples: 'make it dissolve' (NOT 'make it to dissolve'). Common error - study causative verbs!"
        },
        {
          "type": "input",
          "q": "Lab instructor: 'When you put salt in water and it disappears, we say the salt _____ in the water.' (becomes invisible)",
          "a": "dissolves",
          "alts": [],
          "tip": "The salt is still there but you can't see it anymore.",
          "successTip": "'Dissolve' (verb) vs 'solution' (noun). Verb forms: dissolve-dissolved-dissolved (regular). Related: 'soluble' (que se disuelve), 'insoluble' (que no se disuelve). Pattern: -uble endings are adjectives."
        },
        {
          "type": "input",
          "q": "Chemistry teacher: 'In a solution, water is usually the _____ because it dissolves other substances.' (liquid that dissolves things)",
          "a": "solvent",
          "alts": [],
          "tip": "This is the liquid that does the dissolving.",
          "successTip": "'Solvent' (chemical term - disolvente) vs 'solvent' (financial term - solvente). Same spelling, different meanings! Pronunciation same but context completely different. Homophones with context-dependent meanings."
        },
        {
          "type": "order",
          "q": "Solution principle: 'Si pones azúcar en agua, se disuelve.'",
          "words": ["If", "you", "put", "sugar", "in", "water,", "it", "dissolves."],
          "a": "If you put sugar in water, it dissolves.",
          "extra": ["burns", "freezes"],
          "tip": "Sugar seems to disappear in water.",
          "successTip": "'Sugar' is uncountable in English (NOT 'sugars' for table sugar). Can be countable when meaning types: 'different sugars' (tipos de azúcares). General rule: food items usually uncountable unless referring to portions/types."
        },
        {
          "type": "order",
          "q": "Laboratory process: 'Cuando revuelves sal en agua caliente, se disuelve rápidamente.'",
          "words": ["When", "you", "stir", "salt", "in", "hot", "water,", "it", "dissolves", "quickly."],
          "a": "When you stir salt in hot water, it dissolves quickly.",
          "extra": ["freezes", "burns"],
          "tip": "Heat and stirring both speed up dissolving.",
          "successTip": "'Hot water' vs 'warm water' vs 'cold water'. Temperature adjectives: cold < cool < warm < hot. 'Stir' means move circularly with spoon. 'Mix' is more general. Subtle difference in actions!"
        },
        {
          "type": "order",
          "q": "Chemical mixture: 'Si mezclas arena y agua, la arena no se disuelve.'",
          "words": ["If", "you", "mix", "sand", "and", "water,", "the", "sand", "doesn't", "dissolve."],
          "a": "If you mix sand and water, the sand doesn't dissolve.",
          "extra": ["burns", "melts"],
          "tip": "Not everything dissolves in water.",
          "successTip": "'Sand' is uncountable (la arena - substance). 'A grain of sand' (one piece). Pattern: uncountable nouns need quantity words: 'a bag of sand', 'some sand', 'much sand' (NOT 'many sands')."
        },
        {
          "type": "order",
          "q": "Saturation concept: 'Cuando añades demasiado azúcar al agua, no se disuelve más.'",
          "words": ["When", "you", "add", "too", "much", "sugar", "to", "water,", "no", "more", "dissolves."],
          "a": "When you add too much sugar to water, no more dissolves.",
          "extra": ["all", "everything"],
          "tip": "There's a limit to how much can dissolve.",
          "successTip": "'Too much' (demasiado) vs 'very much' (mucho). 'Too much' = excessive (negative connotation). 'Very much' = a lot (neutral/positive). Pattern: 'too much + uncountable noun', 'too many + countable noun'. Important distinction!"
        }
      ]
    },
    {
      "set": "reading",
      "title": "The Water Cycle: Nature's Recycling System",
      "context": "Reading comprehension about water cycle processes using zero conditional and scientific vocabulary",
      "passage": "Water is constantly moving through Earth's systems in a process called the water cycle. This cycle has no beginning or end—it is a continuous process that has been happening for billions of years.\n\nThe cycle begins with evaporation. When the sun heats water in oceans, lakes, and rivers, it changes from liquid to gas (water vapor). This invisible gas rises into the atmosphere. Plants also release water vapor through their leaves in a process called transpiration. Together, evaporation and transpiration put approximately 500,000 cubic kilometers of water into the atmosphere each year.\n\nAs water vapor rises, it cools. When water vapor cools enough, it condenses into tiny droplets, forming clouds. This process is called condensation. If you see water droplets on the outside of a cold glass, you're observing condensation—water vapor in the air touches the cold surface and turns back into liquid.\n\nWhen clouds become heavy with water droplets, precipitation occurs. Water falls back to Earth as rain, snow, sleet, or hail, depending on the temperature. About 78% of precipitation falls into oceans, while 22% falls on land.\n\nWhen precipitation reaches the ground, three things can happen. Some water flows over the surface as runoff, eventually reaching rivers and streams. Some water seeps into the ground, becoming groundwater. Plants and trees absorb some water through their roots. All of these paths eventually lead back to bodies of water, where the cycle begins again.\n\nThe water cycle is essential for life. It distributes fresh water across the planet, regulates temperature, and supports all ecosystems. Interestingly, the water you drink today is the same water that dinosaurs drank millions of years ago—it just keeps cycling through the system. Climate change is now affecting this cycle, causing more extreme weather patterns, droughts in some regions, and flooding in others.\n\nScientists use the zero conditional to describe the water cycle because these processes always happen under the right conditions. For example: 'If water heats to 100°C, it boils.' 'When water vapor cools, it condenses.' These are scientific facts that never change.",
      "questions": [
        {
          "type": "mc",
          "q": "According to the passage, what happens when the sun heats water in oceans and lakes?",
          "options": [
            "A) The water freezes",
            "B) The water evaporates and becomes water vapor",
            "C) The water sinks to the bottom",
            "D) The water turns into clouds immediately"
          ],
          "a": "B",
          "tip": "Look at the second paragraph for information about the first stage.",
          "successTip": "'According to' (según) introduces information source. Pattern: 'according to + noun/source' (according to the passage/the text/scientists). Formal expression for citing information."
        },
        {
          "type": "mc",
          "q": "What is condensation?",
          "options": [
            "A) When liquid water becomes gas",
            "B) When water falls from clouds",
            "C) When water vapor cools and becomes liquid droplets",
            "D) When water flows over the ground"
          ],
          "a": "C",
          "tip": "The passage gives an everyday example of condensation on a cold glass.",
          "successTip": "'What is...?' questions ask for definitions. Answer pattern: 'It is...' or 'Condensation is...'. When defining processes, use 'when' clauses: 'It is when + subject + verb'."
        },
        {
          "type": "mc",
          "q": "According to the text, what percentage of precipitation falls on land?",
          "options": [
            "A) 78%",
            "B) 50%",
            "C) 22%",
            "D) 100%"
          ],
          "a": "C",
          "tip": "This specific statistic is mentioned in the paragraph about precipitation.",
          "successTip": "Percentage expressions: 'X percent of' (NOT 'X percent from'). Say '22 percent' /pərˈsent/ or use symbol '22%'. Both correct. Write as: 'twenty-two percent' or '22%' (both acceptable)."
        },
        {
          "type": "input",
          "q": "The passage states that plants release water vapor through their leaves in a process called _____.",
          "a": "transpiration",
          "alts": [],
          "tip": "This term is mentioned in the second paragraph alongside evaporation.",
          "successTip": "Suffix '-ation' creates nouns from verbs: transpire → transpiration, evaporate → evaporation, condense → condensation. Learn this pattern to expand vocabulary! Very productive suffix in scientific English."
        },
        {
          "type": "mc",
          "q": "When precipitation reaches the ground, what are the three possible paths mentioned?",
          "options": [
            "A) It evaporates, freezes, or boils",
            "B) It flows as runoff, seeps into ground, or is absorbed by plants",
            "C) It turns into clouds, rain, or snow",
            "D) It goes to oceans, rivers, or lakes"
          ],
          "a": "B",
          "tip": "Look at the fifth paragraph which explains what happens after precipitation.",
          "successTip": "Phrasal verbs in passage: 'seep into' (filtrarse), 'flow over' (fluir sobre), 'lead back to' (regresar a). Scientific texts use many phrasal verbs. Don't ignore them - they're essential vocabulary!"
        },
        {
          "type": "mc",
          "q": "Why do scientists use the zero conditional to describe the water cycle?",
          "options": [
            "A) Because it's the easiest grammar to use",
            "B) Because these processes always happen under the right conditions",
            "C) Because the water cycle only happens sometimes",
            "D) Because scientists like conditionals"
          ],
          "a": "B",
          "tip": "The last paragraph explains this directly.",
          "successTip": "'Under conditions' (bajo condiciones). Fixed expression. We say 'under the right conditions', 'under normal conditions', 'under certain conditions'. Preposition 'under' (NOT 'in') with 'conditions'."
        },
        {
          "type": "input",
          "q": "According to the passage, when clouds become heavy with water droplets, _____ occurs and water falls back to Earth.",
          "a": "precipitation",
          "alts": ["rain", "rainfall"],
          "tip": "This is the general term for water falling from clouds in any form.",
          "successTip": "'Precipitation' (formal/scientific term) includes all forms: rain, snow, sleet, hail. 'Rainfall' (specific to rain only). Academic texts prefer general terms like 'precipitation'. Register matters!"
        },
        {
          "type": "mc",
          "q": "What interesting fact does the passage mention about the water we drink today?",
          "options": [
            "A) It's completely new water",
            "B) It's the same water dinosaurs drank millions of years ago",
            "C) It comes only from rain",
            "D) It was created last year"
          ],
          "a": "B",
          "tip": "This fascinating fact is in the sixth paragraph.",
          "successTip": "'The same... as' or 'the same... that'. Pattern: 'the same water that dinosaurs drank' (defining relative clause). Use 'that' (NOT 'what') after 'the same'. Common error!"
        },
        {
          "type": "mc",
          "q": "How is climate change affecting the water cycle according to the passage?",
          "options": [
            "A) It's stopping the water cycle completely",
            "B) It's making it rain everywhere equally",
            "C) It's causing more extreme weather, droughts, and flooding",
            "D) It has no effect on the water cycle"
          ],
          "a": "C",
          "tip": "Climate change impacts are mentioned near the end of the passage.",
          "successTip": "'Cause' vs 'because': 'cause' (verb - causar), 'because' (conjunction - porque). Pattern: 'X causes Y' (NOT 'X causes to Y'). Common verb, transitive, no preposition needed."
        },
        {
          "type": "input",
          "q": "Water that flows over the surface after precipitation is called _____.",
          "a": "runoff",
          "alts": [],
          "tip": "This term describes surface water flow mentioned in the fifth paragraph.",
          "successTip": "Compound noun: 'runoff' (one word in noun form). Related phrasal verb: 'run off' (two words as verb). Pattern: many phrasal verbs become one-word nouns. Examples: 'break down' → 'breakdown', 'take off' → 'takeoff'."
        }
      ]
    },
    {
      "set": "review",
      "title": "Unit 7 Review: Science & Zero Conditional",
      "context": "Comprehensive review of scientific vocabulary, zero conditional structure, and laboratory terminology from entire unit",
      "questions": [
        {
          "type": "match",
          "q": "Review: Match the scientific term with its definition.",
          "matches": [
            {"left": "Hypothesis", "right": "A testable prediction about an experiment"},
            {"left": "Condensation", "right": "Gas changes into liquid when cooled"},
            {"left": "Density", "right": "How much mass is in a given volume"},
            {"left": "Solution", "right": "Mixture where one substance dissolves in another"}
          ],
          "tip": "These are key scientific concepts from this unit.",
          "successTip": "Review word families: hypothesis (noun) - hypothesize (verb) - hypothetical (adjective). Condense (verb) - condensation (noun). Solve (verb) - solution (noun) - soluble (adjective). Learn all forms!"
        },
        {
          "type": "match",
          "q": "Review: Match the laboratory equipment with its use.",
          "matches": [
            {"left": "Thermometer", "right": "Measures temperature"},
            {"left": "Microscope", "right": "Magnifies small objects"},
            {"left": "Beaker", "right": "Holds liquids during experiments"},
            {"left": "Magnet", "right": "Attracts iron and magnetic metals"}
          ],
          "tip": "Knowing your lab equipment is crucial for safety and accuracy.",
          "successTip": "Verb collocations with equipment: 'use a thermometer' (NOT 'work with'), 'look through a microscope' (NOT 'use into'), 'pour into a beaker', 'hold a magnet near'. Learn verb + equipment patterns!"
        },
        {
          "type": "mc",
          "q": "Review: Which sentence correctly uses the zero conditional to describe a scientific fact?",
          "options": [
            "A) If you will heat ice, it will melt.",
            "B) If you heat ice, it melts.",
            "C) If you heated ice, it melted.",
            "D) If you would heat ice, it would melt."
          ],
          "a": "B",
          "tip": "Zero conditional uses present simple in both clauses for general truths.",
          "successTip": "Zero conditional = If/When + present simple, present simple. Common errors: using 'will' (first conditional), using past tense (second conditional). Zero conditional is for scientific facts and things that are ALWAYS true."
        },
        {
          "type": "mc",
          "q": "Review: What happens when you put an object less dense than water into water?",
          "options": [
            "A) It sinks to the bottom",
            "B) It floats on the surface",
            "C) It dissolves",
            "D) It evaporates"
          ],
          "a": "B",
          "tip": "Think about density and buoyancy principles.",
          "successTip": "Comparative structure review: 'less dense THAN water' (NOT 'less dense that'). 'More dense THAN' or 'denser THAN'. Always use 'than' (NOT 'that') after comparatives!"
        },
        {
          "type": "input",
          "q": "Review: When you mix blue paint and yellow paint together, you get _____ paint.",
          "a": "green",
          "alts": [],
          "tip": "Think about primary and secondary colors.",
          "successTip": "'Get' has multiple meanings in English: obtain (get a book), become (get tired), receive (get a gift), arrive (get home), understand (get the joke). Context determines meaning - extremely versatile verb!"
        },
        {
          "type": "input",
          "q": "Review: The force that pulls all objects toward the Earth is called _____.",
          "a": "gravity",
          "alts": [],
          "tip": "This force makes things fall down and keeps us on the ground.",
          "successTip": "Abstract scientific nouns (uncountable): gravity, friction, energy, matter, electricity. No plural forms! Say 'a lot of energy' (NOT 'many energies'), 'less friction' (NOT 'fewer frictions')."
        },
        {
          "type": "order",
          "q": "Review: Complete this zero conditional sentence about water.",
          "words": ["When", "water", "reaches", "100", "degrees", "Celsius,", "it", "boils."],
          "a": "When water reaches 100 degrees Celsius, it boils.",
          "extra": ["freezes", "melts"],
          "tip": "100°C is water's boiling point at normal pressure.",
          "successTip": "Temperature collocations: 'reach a temperature', 'rise to a temperature', 'drop to a temperature', 'maintain a temperature'. Verbs with temperature are specific - learn these patterns!"
        },
        {
          "type": "order",
          "q": "Review: Express this scientific fact using zero conditional.",
          "words": ["If", "you", "put", "oil", "in", "water,", "it", "floats."],
          "a": "If you put oil in water, it floats.",
          "extra": ["sinks", "dissolves"],
          "tip": "Oil's density is lower than water's density.",
          "successTip": "'If' and 'when' are interchangeable in zero conditional for scientific facts. Both correct: 'If you heat water...' = 'When you heat water...'. Slight nuance: 'when' suggests more certainty."
        },
        {
          "type": "order",
          "q": "Review: Create a zero conditional sentence about dissolving.",
          "words": ["When", "you", "put", "sugar", "in", "hot", "water,", "it", "dissolves", "quickly."],
          "a": "When you put sugar in hot water, it dissolves quickly.",
          "extra": ["freezes", "boils"],
          "tip": "Heat speeds up the dissolving process.",
          "successTip": "Adverb placement: 'quickly' (manner adverb) goes AFTER the verb or at end of sentence. 'It quickly dissolves' or 'it dissolves quickly' (both OK). Most manner adverbs flexible in position."
        },
        {
          "type": "mc",
          "q": "Review: Which of these is NOT a state of matter?",
          "options": [
            "A) Solid",
            "B) Liquid",
            "C) Gas",
            "D) Dissolve"
          ],
          "a": "D",
          "tip": "Three states of matter are taught at this level.",
          "successTip": "Don't confuse word forms! 'Dissolve' (verb - disolver) vs 'solution' (noun - solución). States of matter are nouns: solid, liquid, gas. Processes are verbs: freeze, melt, boil, dissolve. Learn part of speech!"
        }
      ]
    }
  ]
}
